import React from 'react';
import { useAuth } from './AuthContext';
import { validateFirebaseConfig } from './firebase';
import Login from './Login';
import AccessDenied from './AccessDenied';
import SessionExpired from './SessionExpired';
import './auth.css';

const LOCAL_AUTH_BYPASS = String(process.env.REACT_APP_AUTH_BYPASS || '').toLowerCase() === 'true';

function FirebaseConfigError({ missing }) {
  return (
    <div className="signal-auth-root" data-testid="firebase-config-error">
      <div className="signal-auth-bg" aria-hidden="true" />
      <div className="signal-auth-card">
        <div className="signal-auth-brand">
          <div className="signal-auth-mark signal-auth-mark-red" />
          <div className="signal-auth-brand-text">
            <div className="signal-auth-logo">AUTH NOT CONFIGURED</div>
            <div className="signal-auth-tagline">FIREBASE ENV MISSING</div>
          </div>
        </div>
        <div className="signal-auth-sub">
          The control plane cannot start because the following Firebase environment variables are missing from this deployment:
        </div>
        <ul className="signal-auth-missing-list">
          {missing.map((k) => <li key={k}><code>{k}</code></li>)}
        </ul>
        <div className="signal-auth-footnote" style={{ marginTop: 16 }}>
          Set these in your deployment (Vercel Project → Settings → Environment Variables → Production), redeploy, then reload this page. Values are available from Firebase Console → Project Settings → General → SDK config.
        </div>
      </div>
    </div>
  );
}

/**
 * Default-deny frontend gate. Renders nothing protected until auth state is
 * resolved AND the user has a valid role. Otherwise shows the appropriate
 * auth screen (login / access-denied / session-expired / config-error).
 */
export default function AuthGate({ children }) {
  if (LOCAL_AUTH_BYPASS) {
    return children;
  }

  const { ready, user, role, authError, sessionExpired, clearSessionExpired } = useAuth();

  const cfg = validateFirebaseConfig();
  if (!cfg.ok) {
    return <FirebaseConfigError missing={cfg.missing} />;
  }

  if (!ready) {
    return (
      <div className="signal-auth-loading" data-testid="auth-loading">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="signal-auth-loading-spinner" />
          <div>VERIFYING SESSION</div>
        </div>
      </div>
    );
  }

  if (sessionExpired && !user) {
    return <SessionExpired onContinue={clearSessionExpired} />;
  }

  if (!user) {
    return <Login bootstrapError={authError} />;
  }

  if (!role) {
    return <AccessDenied />;
  }

  return children;
}
